import type {RelayFailure} from '@github-ui/ahp-relay/relay-failure'
import {sendCustomMetric} from '@github-ui/stats'

import {commonTags, createEmitter, type MetricSender} from './browser-outcome-metrics'
import {isOneCopilotBrowserTelemetryEnabled} from './telemetry-gate'
import type {UiReadinessSurface} from './ui-readiness-contract'

/**
 * Error codes for errors shown to the user, named after the error states in
 * github/copilot-experiences#12891. Codes come from structured state, never from message text.
 */
export const USER_ERROR_CODES = [
  'access_denied',
  'entitlement_invalid',
  'billing_selection_required',
  'quota_exhausted',
  'rate_limited',
  'task_create_failed',
  'attachment_rejected',
  'repository_denied',
  'task_unavailable',
  'history_load_failed',
  'session_connect_failed',
  'environment_unavailable',
  'runtime_update_required',
  'message_submit_failed',
  'mutation_failed',
  'other',
  'unknown',
] as const
export type UserErrorCode = (typeof USER_ERROR_CODES)[number]

export interface UserVisibleError {
  readonly code: UserErrorCode
  readonly surface?: UiReadinessSurface
  readonly taskId?: string
}

export function userErrorCodeFromRelayFailure(failure: RelayFailure | undefined): UserErrorCode {
  if (!failure) return 'unknown'
  switch (failure.kind) {
    case 'http':
      if (failure.code === 'copilot_license_required') return 'entitlement_invalid'
      if (
        failure.code === 'environment_compute_missing' ||
        failure.code === 'environment_compute_terminal' ||
        failure.code === 'environment_unreachable'
      )
        return 'environment_unavailable'
      if (failure.status === 401 || failure.status === 403) return 'access_denied'
      if (failure.status === 404) return 'task_unavailable'
      if (failure.status === 429) return 'rate_limited'
      return 'session_connect_failed'
    case 'rpc':
      if (failure.subtype === 'sessionNotFound') return 'task_unavailable'
      if (failure.subtype === 'cliVersionMismatch') return 'runtime_update_required'
      return 'session_connect_failed'
    case 'protocol-version':
      return 'runtime_update_required'
    case 'provisioning':
    case 'wake-timeout':
      return 'environment_unavailable'
    case 'transport':
    case 'resumption-timeout':
      return 'session_connect_failed'
    case 'cancelled':
    case 'unknown':
      return 'unknown'
  }
}

export function createUserErrorMetrics({
  send = sendCustomMetric,
  reportError = () => undefined,
  enabled = isOneCopilotBrowserTelemetryEnabled,
}: {
  send?: MetricSender
  reportError?: () => void
  enabled?: () => boolean
} = {}) {
  const emit = createEmitter(send, reportError)
  return {
    shown({code, surface = 'unknown', taskId}: UserVisibleError) {
      if (!enabled()) return
      const fact = {surface, role: 'unknown', ...(taskId ? {task_id: taskId} : {})}
      emit({name: 'ONE_COPILOT_WEB_USER_ERROR', value: 1, tags: {...commonTags(fact), error_code: code}}, fact)
    },
  }
}

export const userErrorMetrics = createUserErrorMetrics()
